import { useState, useMemo, useEffect, Fragment } from 'react';
import { useLocation, Navigate, useNavigate, Link } from 'react-router-dom';

import Button from '../../../components/inputs/Button';
import MyOrder from '../../../components/payments/MyOrder';
import AddOnCard from './AddOnCard';
import Tab from '../../../components/Tab';
import tabs from './tabs';
import { useOrder } from '../../../hooks/useOrder';
import { useAuth } from '../../../hooks/useAuth';
import { getProduct } from '../../../helpers/products';
import { SET_ORDER_DETAILS, SET_PRODUCTS } from '../../../hooks/useOrder';
import InsetLoadingOverlay from '../../../components/InsetLoadingOverlay';
import { isStripePaymentRequired } from '../../../utils/featureFlags';

export default function AudienceUpsell() {
  const [selectedTab, setSelectedTab] = useState('Identifiers');
  const navigate = useNavigate();
  const location = useLocation();

  // Skip upsell if Stripe payment is not required
  useEffect(() => {
    if (!isStripePaymentRequired()) {
      const orderDetails = location.state?.orderDetails || {
        orderId: new URLSearchParams(window.location.search).get('orderId'),
        coreProductName: new URLSearchParams(window.location.search).get('coreProductName'),
        audienceName: new URLSearchParams(window.location.search).get('audienceName'),
        recordsIncluded: Number(new URLSearchParams(window.location.search).get('recordsIncluded')),
      };
      
      navigate('../', {
        state: { orderDetails }
      });
      return;
    }
  }, [navigate, location.state]);

  const selectedTabContent = useMemo(
    () => tabs.find(({ label }) => label === selectedTab),
    [selectedTab]
  );

  const {
    state,
    dispatch,
    openOrder,
    loadOrder,
    loading,
    loadingRemoveFromOrder,
    loadingAddOnToOrder,
  } = useOrder();
  const { orderStatus, products } = state;

  const { user } = useAuth();

  const orderDetails = useMemo(() => {
    let orderDetails = location.state?.orderDetails;
    if (!orderDetails) {
      const params = new URLSearchParams(window.location.search);
      return {
        orderId: params.get('orderId'),
        coreProductName: params.get('coreProductName'),
        audienceName: params.get('audienceName'),
        recordsIncluded: Number(params.get('recordsIncluded')),
      };
    }
    return orderDetails;
  }, [location.state]);

  const {
    orderId,
    coreProductName,
    audienceName,
    recordsIncluded,
    // PR 2d: the saved Audience's id. Needed by the server to look up the
    // live filters and persist them on Order.audienceSnapshot. Without this
    // the snapshot capture short-circuits and the admin CSV columns stay
    // empty (Audience Summary, Snapshot Captured At).
    audience,
    // PR 2d: optional client-rendered plain-language summary, captured
    // here so it can ride along into openOrder and be persisted on the
    // Order's audienceSnapshot for debugging.
    audiencePlainText,
  } = orderDetails;

  useEffect(() => {
    dispatch({
      type: SET_ORDER_DETAILS,
      payload: orderDetails,
    });
  }, [orderDetails, dispatch]);

  useEffect(() => {
    if (orderDetails.coreProductName && orderStatus == null && !orderId) {
      const product = getProduct(orderDetails.coreProductName, user?.plan);
      dispatch({
        type: SET_PRODUCTS,
        payload: [{ ...product, quantity: orderDetails.recordsIncluded }],
      });
    }
  }, [dispatch, orderDetails, orderId, orderStatus, user?.plan]);

  useEffect(() => {
    if (orderId && orderStatus == null) {
      loadOrder({
        variables: {
          orderId,
          expandInvoice: true,
        },
      });
    }
  }, [loadOrder, orderId, orderStatus]);

  useEffect(() => {
    if (products?.length > 0 && orderStatus == null && !orderId) {
      openOrder({
        variables: {
          products,
          orderType: coreProductName,
          orderDetails: {
            audienceName,
            recordsIncluded,
            // PR 2d: forward the Audience id when present so the server
            // can capture the snapshot. Forwarded conditionally because
            // legacy flows that hit this page (e.g. resumed/recovered
            // orders) may not carry it through location.state.
            ...(audience ? { audience } : {}),
            // PR 2d: forwarded only when present; the server's
            // OrderDetailsInput accepts it as optional.
            ...(audiencePlainText ? { audiencePlainText } : {}),
          },
        },
      });
    }
  }, [
    audience,
    audienceName,
    audiencePlainText,
    coreProductName,
    openOrder,
    orderId,
    orderStatus,
    products,
    recordsIncluded,
  ]);

  if (!coreProductName || !audienceName || !recordsIncluded) {
    return <Navigate to="../../../mywork/audiences" />;
  }

  return (
    <div className="relative w-full h-full flex flex-col overflow-auto py-3 px-6">
      {loading && <InsetLoadingOverlay message="Setting up your order..." />}
      <div className="flex flex-col">
        <p className="uppercase text-primary font-bold mb-2 text-sm">
          One Last Step
        </p>
        <h3>Select Your Enhancement Package</h3>
        <p className="w-1/2">
          Select the ideal package of elements to append to your customer or
          prospect file to unlock personalization, segmentation, and new methods
          of outreach.
        </p>
      </div>
      <div className="flex flex-row flex-grow overflow-auto">
        <div className="flex flex-col flex-grow pr-3">
          <div className="flex flex-row w-full mt-6 border-b">
            {tabs.map(({ label }, idx) => (
              <Fragment key={idx}>
                <Tab
                  label={label}
                  selected={selectedTab === label}
                  onClick={() => setSelectedTab(label)}
                  key={idx}
                />
                {idx < tabs.length - 1 ? (
                  <div className="h-full flex mx-6">
                    <div className="w-px bg-gray-200 h-4/5"></div>
                  </div>
                ) : null}
              </Fragment>
            ))}
          </div>
          <div className="flex flex-row flex-grow overflow-auto">
            <div
              className="flex flex-grow-0 grid grid-cols-3 xl:grid-cols-4 gap-4 2xl:gap-5 mt-12 py-3 2xl-py-5"
              style={{ height: 'fit-content' }}
            >
              {selectedTabContent?.addOns?.map((addOn) => (
                <AddOnCard {...addOn} key={addOn.productKey} />
              ))}
            </div>
          </div>
          <div className="flex flex-row justify-between py-3">
            <Link to="../../audiencebuilder">
              <Button secondary small disabled={loading}>
                Back
              </Button>
            </Link>
            {products?.length === 1 && (
              <Button
                secondary
                small
                onClick={() => navigate('../')}
                disabled={
                  loading || loadingRemoveFromOrder || loadingAddOnToOrder
                }
              >
                Skip
              </Button>
            )}
          </div>
        </div>
        <div className="xl:w-96">
          <MyOrder showCoreProductDetails={false} />
        </div>
      </div>
    </div>
  );
}
